/* eslint-disable */
import { getLocale, experimentalStaticLocale } from '../runtime.js';

/** @typedef {import('../runtime.js').LocalizedString} LocalizedString */

/** @typedef {{ email: NonNullable<unknown> }} Legal_Withdrawal_BodyInputs */

const en_legal_withdrawal_body = /** @type {(inputs: Legal_Withdrawal_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`If you are a consumer in the EU, you can generally withdraw from a contract made at a distance within **14 days**, with no reason and no cost beyond the law's limits. This page explains how that works for what you buy from businesses on Complete Athlete. Because the business is the seller, you withdraw from your contract with that business, not with us.

## 1. When the 14 days start

- **Services and digital content:** from the day the contract is made.
- **Physical products:** from the day you receive the product.

## 2. When the right does not apply

- A session, class or event **booked for a specific date or period**, because it is a sports or leisure service with a set date.
- A service that has been **fully performed**, if you agreed beforehand that it could start and accepted that you would then lose the right.
- A digital file you **started downloading**, if you agreed beforehand to immediate delivery and accepted that you would then lose the right, and received confirmation of this.
- A product made to your specification.

The right normally does apply to entry packs and client subscriptions with no fixed date, plans not yet delivered, and shop products.

## 3. How to withdraw

Tell the business before the 14 days end. The quickest way is **Withdraw from contract here** on the purchase in [My payments](/my-payments), or under Purchases in the mobile app: you confirm it, we email you a confirmation with the date and time, and the business is told. You can also use the form below or any clear statement, sent to the contact details on the business's page. If you cannot reach the business, write to us at ${i?.email} or through the [contact form](/contact), and we will pass it on.

## 4. What happens next

The business refunds you within 14 days, using the same payment method, including standard delivery costs. For a service you asked to start during the 14 days, you pay for what was provided up to the moment you withdrew. You return a physical product within 14 days and pay for returning it, unless the business agreed otherwise.

Separately from withdrawal, a business's cancellation policy applies to its bookings and is shown before you book. You can find it in [the terms](/terms).

## 5. Model withdrawal form

*(complete and return this form only if you wish to withdraw from the contract)*

- To: [name of the business, postal address and email]
- I/We hereby give notice that I/we withdraw from my/our contract for the sale of the following goods / provision of the following service: [ ]
- Date the contract was made / goods received: [ ]
- Name of consumer(s): [ ]
- Address of consumer(s): [ ]
- Signature of consumer(s) (only if this form is sent on paper): [ ]
- Date: [ ]`)
};

const pl_legal_withdrawal_body = /** @type {(inputs: Legal_Withdrawal_BodyInputs) => LocalizedString} */ (i) => {
	return /** @type {LocalizedString} */ (`Jeśli jesteś konsumentem w UE, co do zasady możesz odstąpić od umowy zawartej na odległość w terminie **14 dni**, bez podawania przyczyny i bez ponoszenia kosztów innych niż przewidziane w przepisach. Ta strona wyjaśnia, jak wygląda to w przypadku tego, co kupujesz od firm w Complete Athlete. Ponieważ sprzedawcą jest firma, odstępujesz od umowy zawartej z tą firmą, a nie z nami.

## 1. Kiedy zaczyna biec termin 14 dni

- **Usługi i treści cyfrowe:** od dnia zawarcia umowy.
- **Towary:** od dnia, w którym otrzymasz towar.

## 2. Kiedy prawo odstąpienia od umowy nie przysługuje

- Wizyta, zajęcia lub wydarzenie **zarezerwowane na określony dzień lub okres**, ponieważ jest to usługa związana z wydarzeniami sportowymi lub wypoczynkiem z oznaczonym terminem.
- Usługa **wykonana w pełni**, jeżeli nastąpiło to za Twoją uprzednią zgodą na jej rozpoczęcie i przyjęcie przez Ciebie do wiadomości, że utracisz wtedy prawo odstąpienia od umowy.
- Plik cyfrowy, którego **pobieranie zostało rozpoczęte**, jeżeli nastąpiło to za Twoją uprzednią zgodą na natychmiastowe dostarczenie i przyjęcie przez Ciebie do wiadomości, że utracisz wtedy prawo odstąpienia od umowy, a firma przekazała Ci potwierdzenie tych okoliczności.
- Towar wyprodukowany według Twojej specyfikacji.

Prawo to co do zasady przysługuje w przypadku karnetów i subskrypcji u firmy bez oznaczonego terminu, planów jeszcze niedostarczonych oraz produktów ze sklepu.

## 3. Jak odstąpić od umowy

Złóż firmie oświadczenie o odstąpieniu od umowy przed upływem terminu 14 dni. Najszybciej zrobisz to przyciskiem **Odstąp od umowy tutaj** przy zakupie w [Moich płatnościach](/my-payments) albo w sekcji Zakupy w aplikacji mobilnej: potwierdzasz, wysyłamy Ci e-mail z datą i godziną, a firma dostaje informację. Możesz też skorzystać z formularza poniżej lub z dowolnego jednoznacznego oświadczenia, wysyłając je na dane kontaktowe podane na stronie firmy. Jeśli nie możesz skontaktować się z firmą, napisz do nas na adres ${i?.email} lub przez [formularz kontaktowy](/contact), a my przekażemy Twoje oświadczenie.

## 4. Co dzieje się dalej

Firma zwraca Ci płatności w terminie 14 dni, przy użyciu takiego samego sposobu zapłaty, w tym koszty standardowego dostarczenia towaru. Za usługę, której rozpoczęcia zażądasz w terminie 14 dni, płacisz za świadczenia spełnione do chwili odstąpienia od umowy. Towar zwracasz w terminie 14 dni i ponosisz koszty jego zwrotu, chyba że firma zgodziła się na inne rozwiązanie.

Niezależnie od odstąpienia od umowy do rezerwacji w danej firmie mają zastosowanie jej zasady odwołania, które widzisz przed rezerwacją. Znajdziesz je w [Regulaminie](/terms).

## 5. Wzór formularza odstąpienia od umowy

*(formularz ten należy wypełnić i odesłać tylko w przypadku chęci odstąpienia od umowy)*

- Adresat: [nazwa firmy, pełny adres pocztowy oraz adres e-mail]
- Ja/My(\\*) niniejszym informuję/informujemy(\\*) o moim/naszym odstąpieniu od umowy sprzedaży następujących towarów(\\*) umowy dostawy następujących towarów(\\*) umowy o dzieło polegającej na wykonaniu następujących towarów(\\*)/o świadczenie następującej usługi(\\*): [ ]
- Data zawarcia umowy(\\*)/odbioru(\\*): [ ]
- Imię i nazwisko konsumenta(-ów): [ ]
- Adres konsumenta(-ów): [ ]
- Podpis konsumenta(-ów) (tylko jeżeli formularz jest przesyłany w wersji papierowej): [ ]
- Data: [ ]

(\\*) Niepotrzebne skreślić.`)
};

/**
* | output |
* | --- |
* | "If you are a consumer in the EU, you can generally withdraw from a contract made at a distance within **14 days**, with no reason and no cost beyond the law'..." |
*
* @param {Legal_Withdrawal_BodyInputs} inputs
* @param {{ locale?: "en" | "pl" }} options
* @returns {LocalizedString}
*/
export const legal_withdrawal_body = /** @type {((inputs: Legal_Withdrawal_BodyInputs, options?: { locale?: "en" | "pl" }) => LocalizedString) & import('../runtime.js').MessageMetadata<Legal_Withdrawal_BodyInputs, { locale?: "en" | "pl" }, {}>} */ ((inputs, options = {}) => {
	const locale = experimentalStaticLocale ?? options.locale ?? getLocale()
	if (locale === "pl") return pl_legal_withdrawal_body(inputs)
	return en_legal_withdrawal_body(inputs)
});